import {
  AI_MODELING_EFFECT_DURATION_MS,
  AI_MODELING_EFFECT_EASING,
  calcElementDelayMs,
} from './aiModelingEffectTiming';

/**
 * まだ1つも現れていない間の待ち時間。伏せている要素がないので長く待っても副作用がない。
 * ビューごと新規に作られる addView では、モデルの読み込みを挟むぶん描画までに時間がかかる。
 */
const INITIAL_WAIT_TIMEOUT_MS = 3000;
/**
 * 1つ現れた後の待ち時間。見つかった要素は順番を決めるまで伏せているため、
 * 座標を持たず描画されない要素が混ざると、その間だけ既存の表示が消えて見える。
 */
const PARTIAL_WAIT_TIMEOUT_MS = 300;

/** 持ち上げる距離。キャンバス座標なので、拡大率によらず要素の大きさとの比率が保たれる。 */
const APPEAR_RISE = 20;

const APPEAR_KEYFRAMES: Keyframe[] = [
  { opacity: 0, transform: `translateY(${APPEAR_RISE}px)` },
  { opacity: 1, transform: 'translateY(0)' },
];

const FLASH_DURATION_MS = 800;
/** フェードインの途中から明るくし始める。出切ってからだと2つの動きが分かれて見える。 */
const FLASH_OFFSET_MS = 200;
const FLASH_KEYFRAMES: Keyframe[] = [
  { filter: 'brightness(1) contrast(1)' },
  { filter: 'brightness(1.08) contrast(1.05)', offset: 0.3 },
  { filter: 'brightness(1) contrast(1)' },
];
/** filter は要素数ぶん重い。目で変更点を追える数のときだけ出す。 */
const MAX_FLASH_ELEMENTS = 60;

/**
 * AIが作った要素を、画面の左上に近いものから順に持ち上げながらフェードインさせ、そのあと一瞬だけ明るくする。
 * RTDBの反映とReactの再描画を経て要素が現れるため、DOMを直接掴んでアニメーションさせる。
 */
export function runAIModelingAppearEffect(elementIds: readonly string[]): void {
  if (elementIds.length === 0) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // 同じIDが重複していると found が揃わず、待ち時間いっぱいまで伏せたままになる
  const uniqueIds = [...new Set(elementIds)];
  const startedAt = performance.now();
  let firstFoundAt: number | null = null;
  // ゾーンは本体と枠が別々に描かれるなど、1つの要素が複数のDOMに分かれることがある
  const found = new Map<string, SVGElement[]>();

  const tick = () => {
    for (const id of uniqueIds) {
      if (found.has(id)) continue;

      const elements = Array.from(document.querySelectorAll(`[data-model-element-id="${CSS.escape(id)}"]`)).filter(
        (element): element is SVGElement => element instanceof SVGElement
      );
      if (elements.length === 0) continue;

      // 並び順は全要素が揃ってからでないと決められない。それまで素のまま見えないよう伏せておく
      for (const element of elements) element.style.opacity = '0';
      found.set(id, elements);
    }

    if (found.size > 0 && firstFoundAt === null) firstFoundAt = performance.now();

    const waited = performance.now() - (firstFoundAt ?? startedAt);
    const limit = firstFoundAt === null ? INITIAL_WAIT_TIMEOUT_MS : PARTIAL_WAIT_TIMEOUT_MS;
    if (found.size < uniqueIds.length && waited < limit) {
      requestAnimationFrame(tick);
      return;
    }

    startAppearInSpatialOrder([...found.values()]);
  };

  requestAnimationFrame(tick);
}

/** 画面上で左上に近い要素から順に出現させる。 */
function startAppearInSpatialOrder(groups: readonly SVGElement[][]): void {
  // getBoundingClientRect はレイアウトを強制するため、アニメーションを挟まずまとめて読む
  const ordered = groups
    .map((elements) => {
      const { left, top } = elements[0].getBoundingClientRect();
      return { elements, distance: left + top };
    })
    .sort((a, b) => a.distance - b.distance);

  const flashes = ordered.length <= MAX_FLASH_ELEMENTS;

  ordered.forEach(({ elements }, index) => {
    const delay = calcElementDelayMs(index, ordered.length);

    for (const element of elements) {
      element.style.opacity = '';
      element.animate(APPEAR_KEYFRAMES, {
        duration: AI_MODELING_EFFECT_DURATION_MS,
        delay,
        easing: AI_MODELING_EFFECT_EASING,
        // 遅延中も透明にしておく。これがないと一度表示されてから消えて出直す動きになる
        fill: 'backwards',
      });

      // fill を指定しない。終わったあとに filter を残すと、要素の描画が常にラスタライズされ続ける
      if (flashes) {
        element.animate(FLASH_KEYFRAMES, {
          duration: FLASH_DURATION_MS,
          delay: delay + FLASH_OFFSET_MS,
          easing: 'ease-out',
        });
      }
    }
  });
}
